import useUrlPagination from 'hooks/use-url-pagination'
import { tablePaginationInUrl } from 'utils/url-pagination'
import TextColumnFilter from 'components/atoms/text-column-filter'
import TableBody from 'components/molecules/table-body'
import TableBodyEmptyData from 'components/molecules/table-body-empty-data'
import TableHeader from 'components/molecules/table-header'
import TablePagination from 'components/molecules/table-pagination'
import PropTypes from 'prop-types'
import React, { useMemo, useEffect, useRef } from 'react'
import {
  useFilters,
  usePagination, useTable
} from 'react-table'
import { Table } from 'reactstrap'


const proptypes = {
  data            : PropTypes.array.isRequired,
  columns         : PropTypes.array.isRequired,
  hiddenColumns   : PropTypes.array,
  showPagination  : PropTypes.bool,
  defaultPageSize : PropTypes.number,
  getRowProps     : PropTypes.func
}

const defaultValues = {
  data            : [],
  columns         : [],
  hiddenColumns   : [],
  showPagination  : true,
  defaultPageSize : 10,
  getRowProps     : () => { }
}

/**
 * Interface table with ReactTable Functionality. Only for static data (no fetch data from server)
 * @param {Array<Object>} data array of seller_inbound objects
 * @param {Array<Object} columns memoized columns array object
 * @param {Array<string>} hiddenColumns hide columns not relavant to the page
 * @param {func} getRowProps - function to returns an object to change the <tr> element
 */
const DataTableStatic = ({
  data,
  columns,
  hiddenColumns,
  showPagination,
  defaultPageSize,
  getRowProps
}) => {
  const urlPagination = useUrlPagination({ enabled: (path) => showPagination && tablePaginationInUrl(path), defaultPageSize: defaultPageSize })
  // default filter for all filterable columns
  const defaultColumn = useMemo(
    () => ({
      Filter: TextColumnFilter
    }),
    []
  )

  /* Initialize table instance */
  // useTable at the very least needs to be provided with an object containing the memoized
  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
      autoResetPage: !urlPagination.active,
      initialState: {
        hiddenColumns : hiddenColumns,
        pageSize      : urlPagination.active ? urlPagination.pageSize : defaultPageSize,
        pageIndex     : urlPagination.active ? urlPagination.page - 1 : 0
      }
    },
    useFilters,
    usePagination
  )

  /* Minimum configuration of react table */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // Instead of using 'rows', we'll use page, which has only the rows for the active page
    prepareRow,
    allColumns,
    state: { pageIndex, pageSize, filters },
    /* pagination */
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    setPageSize,
    nextPage,
    previousPage
  } = tableInstance
  const previousFilters = useRef(JSON.stringify(filters))
  useEffect(() => {
    if (!urlPagination.active) return
    if (pageIndex !== urlPagination.page - 1) gotoPage(urlPagination.page - 1)
    if (pageSize !== urlPagination.pageSize) setPageSize(urlPagination.pageSize)
  }, [ urlPagination.active, urlPagination.page, urlPagination.pageSize, pageIndex, pageSize, gotoPage, setPageSize ])
  useEffect(() => {
    const signature = JSON.stringify(filters)
    if (signature !== previousFilters.current && urlPagination.active) urlPagination.setPage(1)
    previousFilters.current = signature
  }, [ filters, urlPagination.active, urlPagination.setPage ])


  return (
    <>
      <Table
        size='sm'
        hover
        striped
        responsive
        { ...getTableProps() }
      >
        <TableHeader
          headerGroups={ headerGroups }
        />
        {
          data.length > 0
            ? (
              <TableBody
                rows={ page }
                prepareRow={ prepareRow }
                getTableBodyProps={ getTableBodyProps }
                getRowProps={ getRowProps }
              />
            )
            : <TableBodyEmptyData totalColumns={ allColumns.length } />
        }
      </Table>
      {
        showPagination
          ? (
            <div className='p-3'>
              <TablePagination
                canPreviousPage={ canPreviousPage }
                canNextPage={ canNextPage }
                pageCount={ pageCount }
                pageIndex={ pageIndex }
                pageSize={ pageSize }
                gotoPage={ urlPagination.active ? (index) => urlPagination.setPage(index + 1) : gotoPage }
                setPageSize={ urlPagination.active ? urlPagination.setPageSize : setPageSize }
                nextPage={ urlPagination.active ? () => urlPagination.setPage(urlPagination.page + 1) : nextPage }
                previousPage={ urlPagination.active ? () => urlPagination.setPage(urlPagination.page - 1) : previousPage }
              />
            </div>
          )
          : null
      }
    </>
  )
}

DataTableStatic.propTypes    = proptypes
DataTableStatic.defaultProps = defaultValues

export default DataTableStatic
